/** The layout of a record's page: a header with its key facts and **one** status path (with the evidence chain folded under it), five tabs, a main
 * column and a right rail of cards - ADR-0212 (the record page "with the document view beside the
 * data").
 *
 * Top to bottom: the **header card** (the kind's icon, the title as the page's `<h1>`, a small label,
 * actions, a row of key facts, and the status path of chevron steps); the **tabs** - Details,
 * Document, Calculation, Approvals, History - which are the product's one tablist (`RecordTabs`:
 * the panels swap in place, so it is a real ARIA tablist with a roving tab stop, arrow keys that wrap
 * and a live region that exists from the first render); then the **main column** (the current
 * tab's content, `children`) beside the **right rail** of three cards, Document, Related and
 * Activity, which stay in view whichever tab is open.
 *
 * It lays out and decides nothing: the key figure, the facts, the path and every card's content are the
 * caller's, and a card with no content is not drawn (a rail card nobody fills is a control that does
 * nothing). The figure is the page's headline number - 28px, weight 700, its unit in the case it was
 * written (`tCO2e` is never uppercased) - and it is a string the server sent; a figure that has a
 * receipt is opened from the Calculation tab, as every figure's receipt is.
 */

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import type { Fact } from "./KeyFacts";
import { PageTitle } from "./PageTitle";
import { RecordPanel, RecordTabs } from "../../screens/RecordTabs";
import styles from "./RecordPage.module.css";

export type RecordTabKey = "details" | "document" | "calculation" | "approvals" | "history";

export const RECORD_TABS: readonly { key: RecordTabKey; label: string }[] = [
  { key: "details", label: "Details" },
  { key: "document", label: "Document" },
  { key: "calculation", label: "Calculation" },
  { key: "approvals", label: "Approvals" },
  { key: "history", label: "History" },
];

export interface RecordRail {
  document?: ReactNode;
  related?: ReactNode;
  activity?: ReactNode;
}

/** The page's headline number: a label ("Emissions"), the value as sent, and its unit. */
export interface RecordFigure {
  label: string;
  value: string;
  /** Written as it should read: `tCO2e`. Never uppercased by the page. */
  unit?: string;
}

export interface RecordPageProps {
  icon: Icon;
  title: string;
  /** The kind, small: "Activity record". */
  label?: string;
  actions?: ReactNode;
  figure?: RecordFigure;
  facts?: readonly Fact[];
  /** A `StatusPath`, passed as a node so the page does not decide the steps. */
  path?: ReactNode;
  /** The evidence meter (`EvidenceMeter`), passed as a node for the same reason: the page lays it out
   * between the facts and the path and decides none of its segments. */
  meter?: ReactNode;
  /** Which of the five tabs is showing, and how to change it. */
  tab: RecordTabKey;
  onTab: (key: RecordTabKey) => void;
  /** Fewer than five, for a kind that has no calculation or no approvals. */
  tabs?: readonly { key: RecordTabKey; label: string }[];
  /** What the live region says on a tab change: the tab and what it is about. */
  saying?: string;
  rail?: RecordRail;
  /** The current tab's content. */
  children: ReactNode;
}

const RAIL: readonly { key: keyof RecordRail; title: string }[] = [
  { key: "document", title: "Document" },
  { key: "related", title: "Related" },
  { key: "activity", title: "Activity" },
];

export function RecordPage({
  icon: Glyph,
  title,
  label,
  actions,
  figure,
  facts,
  path,
  meter,
  tab,
  onTab,
  tabs = RECORD_TABS,
  saying,
  rail,
  children,
}: RecordPageProps) {
  const current = tabs.find((entry) => entry.key === tab);

  const cards = rail
    ? RAIL.filter((card) => rail[card.key] !== undefined && rail[card.key] !== null)
    : [];

  return (
    <div className={styles.page} data-record-page>
      <header className={`card ${styles.head}`}>
        <div className={styles.top}>
          <span className={styles.badge} aria-hidden="true">
            <Glyph size={22} weight="duotone" />
          </span>
          <div className={styles.titles}>
            <PageTitle>{title}</PageTitle>
            {label ? <span className={styles.label}>{label}</span> : null}
          </div>
          {figure ? (
            <div className={styles.figure} data-testid="record-figure">
              <span className={styles.figureLabel}>{figure.label}</span>
              <span className={`${styles.figureValue} num`}>
                {figure.value}
                {figure.unit ? <span className={styles.figureUnit}>{figure.unit}</span> : null}
              </span>
            </div>
          ) : null}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
        {facts && facts.length > 0 ? (
          <dl className={styles.facts} data-testid="record-facts">
            {facts.map((fact) => (
              <div key={fact.label} className={styles.fact} data-testid={fact.testId}>
                <dt>{fact.label}</dt>
                <dd className={fact.mono ? styles.mono : undefined}>{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {path ? <div className={styles.path}>{path}</div> : null}
        {meter ? <div className={styles.meter}>{meter}</div> : null}
      </header>

      {/* The product's one tablist (`RecordTabs`): the panels swap in place, so it is a real ARIA
          tablist, and a second implementation is what `source.a11y.test.ts` refuses. */}
      <RecordTabs
        tabs={tabs}
        tab={tab}
        onSelect={(key) => onTab(key as RecordTabKey)}
        label={`${title} sections`}
        saying={saying ?? (current ? `Showing ${current.label}` : "")}
      />

      <div className={`${styles.columns} ${cards.length > 0 ? styles.withRail : ""}`} data-record-columns>
        <RecordPanel tab={tab} className={styles.main}>
          {children}
        </RecordPanel>
        {cards.length > 0 ? (
          <aside className={styles.rail} aria-label="Beside this record">
            {cards.map((card) => (
              <section key={card.key} className={`card ${styles.card}`} data-rail-card={card.key}>
                <h2 className={styles.cardTitle}>{card.title}</h2>
                {rail?.[card.key]}
              </section>
            ))}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
